import { useState, type FormEvent } from 'react';
import { Button, Input, Modal, Select, type SelectOption } from '../../components';
import { ApiError } from '../../lib/api';
import type { LookupOption } from '../leads/types';
import type { ContactPayload, ContactRow } from './types';

export function ContactFormModal({
  row,
  categories,
  onClose,
  onSubmit,
}: {
  row?: ContactRow;
  categories: LookupOption[];
  onClose: () => void;
  onSubmit: (payload: ContactPayload) => Promise<void>;
}) {
  const [full_name, setFullName] = useState(row?.full_name ?? '');
  const [first_name, setFirstName] = useState(row?.first_name ?? '');
  const [last_name, setLastName] = useState(row?.last_name ?? '');
  const [email, setEmail] = useState(row?.email ?? '');
  const [phone, setPhone] = useState(row?.phone ?? '');
  const [city, setCity] = useState(row?.city ?? '');
  const [country, setCountry] = useState(row?.country ?? '');
  const [date_of_birth, setDob] = useState(row?.date_of_birth ?? '');
  const [contact_category_id, setCat] = useState(row?.contact_category_id ?? '');
  const [notes, setNotes] = useState(row?.notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const catOpts: SelectOption[] = categories.map((c) => ({ value: c.id, label: c.name }));

  async function handle(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const s = (v: string) => (v.trim() === '' ? null : v.trim());
      await onSubmit({
        full_name: full_name.trim() || 'Unnamed contact',
        first_name: s(first_name),
        last_name: s(last_name),
        email: s(email),
        phone: s(phone),
        city: s(city),
        country: s(country),
        date_of_birth: s(date_of_birth),
        contact_category_id: s(contact_category_id),
        notes: s(notes),
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={row ? 'Edit contact' : 'New contact'} width={520}>
      <form onSubmit={handle}>
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input id="ct-first" label="First name" value={first_name} onChange={setFirstName} />
            </div>
            <div style={{ flex: 1 }}>
              <Input id="ct-last" label="Last name" value={last_name} onChange={setLastName} />
            </div>
          </div>
          <Input
            id="ct-name"
            label="Display name"
            value={full_name}
            onChange={setFullName}
            required
            autoFocus
          />
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input id="ct-email" label="Email" type="email" value={email} onChange={setEmail} />
            </div>
            <div style={{ flex: 1 }}>
              <Input id="ct-phone" label="Phone" value={phone} onChange={setPhone} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input id="ct-city" label="City" value={city} onChange={setCity} />
            </div>
            <div style={{ flex: 1 }}>
              <Input id="ct-country" label="Country" value={country} onChange={setCountry} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Input
                id="ct-dob"
                label="Date of birth"
                type="date"
                value={date_of_birth}
                onChange={setDob}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Select
                id="ct-cat"
                label="Category"
                placeholder="— none —"
                value={contact_category_id}
                options={catOpts}
                onChange={setCat}
              />
            </div>
          </div>
          <div>
            <label className="field-label" htmlFor="ct-notes">
              Notes
            </label>
            <textarea
              id="ct-notes"
              className="input"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          {error && <div className="alert">{error}</div>}
        </div>
        <div
          className="card-header"
          style={{
            justifyContent: 'flex-end',
            borderTop: '1px solid var(--border)',
            borderBottom: 'none',
          }}
        >
          <Button onClick={onClose} disabled={submitting} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
